import React, { useEffect, useState, useRef } from 'react';
import { host } from './script/variables';
import PartnerHome from './PartnerHome';
import LegalDocuments from './LegalDocuments';
import { LEGAL_VERSION } from './legalContent';

/* Adjust this path to wherever theme.css actually lives in your tree
   (src/theme.css, src/styles/theme.css, etc). If it's already imported
   globally in App.js/index.js, this import is still safe — bundlers
   dedupe repeated CSS imports, so it costs nothing to be explicit here. */
import './style/theme.css';
import './style/partnerLogin.css';
/* ─── Inline styles as a style tag ─────────────────────────────────────────── */


/* ─── Password strength util ──────────────────────────────────────────────── */
function getStrength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8)  s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s; // 0–4
}

/* ─── OTP Input (with paste support) ─────────────────────────────────────── */
function OtpInput({ value, onChange }) {
  const refs = useRef([]);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs.current[i - 1]?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    e.preventDefault();
    const next = value.slice(0, i) + e.key + value.slice(i + 1);
    onChange(next.slice(0, 6));
    if (i < 5) refs.current[i + 1]?.focus();
  };

  // Fix: support paste anywhere on the OTP row
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className="p-otp-row">
      {[0,1,2,3,4,5].map(i => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className={`p-otp-box${value[i] ? ' filled' : ''}`}
          value={value[i] || ''}
          onChange={() => {}} // controlled; real input via onKeyDown / onPaste
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
        />
      ))}
    </div>
  );
}

/* ─── Resend OTP with cooldown ────────────────────────────────────────────── */
const RESEND_COOLDOWN = 30; // seconds — must match the backend cooldown

function ResendOtp({ onResend, loading }) {
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef(null);

  // Start countdown on mount (OTP was just sent)
  useEffect(() => {
    startCountdown();
    return () => clearInterval(timerRef.current);
  }, []);

  const startCountdown = () => {
    setCountdown(RESEND_COOLDOWN);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (countdown > 0 || loading) return;
    await onResend();
    startCountdown();
  };

  return (
    <div className="p-resend-row">
      <span>Didn't receive it?</span>
      {countdown > 0 ? (
        <span className="p-resend-countdown">Resend in {countdown}s</span>
      ) : (
        <button
          type="button"
          className="p-resend-btn"
          onClick={handleResend}
          disabled={loading}
        >
          {loading ? 'Sending…' : 'Resend code'}
        </button>
      )}
    </div>
  );
}

/* ─── Password field ──────────────────────────────────────────────────────── */
function PwField({ label, value, onChange, showStrength, autoComplete = 'current-password', onKeyDown }) {
  const [show, setShow] = useState(false);
  const strength = showStrength ? getStrength(value) : 0;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', 'weak', 'weak', 'medium', 'strong'];

  return (
    <div className="p-field">
      <label className="p-label">{label}</label>
      <div className="p-input-wrap">
        <span className="p-input-icon">🔒</span>
        <input
          type={show ? 'text' : 'password'}
          className="p-input"
          style={{ paddingRight: 38 }}
          value={value}
          autoComplete={autoComplete}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="••••••••"
        />
        <button
          type="button"
          className="p-eye"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow(s => !s)}
        >
          {show ? '🙈' : '👁️'}
        </button>
      </div>
      {showStrength && value && (
        <>
          <div className="p-strength-bar">
            {[1,2,3,4].map(n => (
              <div key={n} className={`p-strength-seg${n <= strength ? ' ' + colors[strength] : ''}`} />
            ))}
          </div>
          <p className="p-strength-label" style={{ color: ['','var(--p-red)','var(--p-red)','var(--p-amber)','var(--p-emerald)'][strength] }}>
            {labels[strength]}
          </p>
        </>
      )}
    </div>
  );
}

/* ─── Message ─────────────────────────────────────────────────────────────── */
function Msg({ text, type }) {
  if (!text) return null;
  const icons = { error: '⚠️', success: '✓', info: 'ℹ' };
  return (
    <div className={`p-msg ${type}`} role={type === 'error' ? 'alert' : 'status'}>
      <span>{icons[type]}</span>
      <span>{text}</span>
    </div>
  );
}

/* ─── Steps ───────────────────────────────────────────────────────────────── */
/* `labels` is now a prop so the reset flow can say "New Password" on the
   last step instead of reusing the registration wording. */
function Steps({ current, labels = ['Email', 'Verify', 'Password'] }) {
  return (
    <div className="p-steps">
      {labels.map((s, i) => (
        <React.Fragment key={i}>
          <div className="p-step">
            <div className={`p-step-dot ${i < current ? 'done' : i === current ? 'active' : ''}`}>
              {i < current ? '✓' : i + 1}
            </div>
            <span className={`p-step-label ${i < current ? 'done' : i === current ? 'active' : ''}`}>{s}</span>
          </div>
          {i < labels.length - 1 && <div className="p-step-line" />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
const Partner = () => {
  /* 'login' | 'register' | 'forgot' — replaces the old boolean isLogin so the
     reset flow is a first-class mode rather than a modal bolted onto login. */
  const [mode, setMode] = useState('login');

  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [cnfpassword, setCnfPassword] = useState('');
  const [otp, setOtp]                 = useState('');
  const [otpSent, setOtpSent]         = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [signupToken, setSignupToken] = useState('');
  const [loading, setLoading]         = useState(false);
  const [msg, setMsg]                 = useState({ text: '', type: 'info' });
  const [isLoggedin, setIsLoggedin]   = useState(false);
  const [lastLoginTime, setLastLoginTime] = useState(null);

  // ── Forgot password ──
  const [resetOtpSent, setResetOtpSent]   = useState(false);
  const [resetVerified, setResetVerified] = useState(false);
  const [resetToken, setResetToken]       = useState('');

  // ── Legal / terms acceptance ──
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showLegal, setShowLegal]         = useState(false);
  const [legalTab, setLegalTab]           = useState('terms');

  const say = (text, type = 'info') => setMsg({ text, type });

  const step       = otpVerified ? 2 : otpSent ? 1 : 0;
  const forgotStep = resetVerified ? 2 : resetOtpSent ? 1 : 0;

  /* ── Auth check ── */
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('partnertoken');
      if (!token) return;
      try {
        const res = await fetch(`${host}/user/partnerAuth`, {
          headers: { 'Content-Type': 'application/json', Authorization: token },
        });
        if (res.ok) setIsLoggedin(true);
        else localStorage.removeItem('partnertoken'); // stale after a password reset
      } catch {}
    })();
  }, []);

  /* ── Mode switching ── */
  const resetAllFields = () => {
    setMsg({ text: '', type: 'info' });
    setEmail(''); setPassword(''); setCnfPassword('');
    setOtp(''); setOtpSent(false); setOtpVerified(false); setSignupToken('');
    setAgreedToTerms(false);
    setResetOtpSent(false); setResetVerified(false); setResetToken('');
  };

  const goTo = (nextMode) => { setMode(nextMode); resetAllFields(); };
  const switchTab = (login) => goTo(login ? 'login' : 'register');

  /* Carry over whatever email they'd already typed on the login form —
     retyping it is the single most annoying part of most reset flows. */
  const goForgot = () => {
    const carried = email;
    goTo('forgot');
    setEmail(carried);
  };

  /* ── Open the legal reader on a given tab ── */
  const openLegal = (tabId = 'terms') => {
    setLegalTab(tabId);
    setShowLegal(true);
  };

  /* ── Login ── */
  const login = async () => {
    if (!email || !password) return say('Please fill in all fields.', 'error');

    setLoading(true);
    say('');
    try {
      const res = await fetch(`${host}/user/loginPartner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const token = res.headers.get('Authorization');
        const lastLogin = data.data?.lastLoginTime;

        setLastLoginTime(lastLogin);
        if (token) localStorage.setItem('partnertoken', token);
        setIsLoggedin(true);
      } else {
        say(data.msg || 'Invalid credentials. Please try again.', 'error');
      }
    } catch {
      say('Connection failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Send signup OTP (reused for initial send + resend) ── */
  const sendOtp = async () => {
    if (!email) return say('Please enter your email.', 'error');
    if (!agreedToTerms) return say('Please review and accept the Terms of Service and Privacy Policy to continue.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/user/sendotp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await res.json();
      if (res.ok) {
        setOtpSent(true);
        setOtp(''); // clear old digits on resend
        say(d.msg || 'OTP sent to your email.', 'success');
      } else {
        say(d.msg || 'Failed to send OTP.', 'error');
      }
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify signup OTP ── */
  const verifyOTP = async () => {
    if (otp.replace(/\s/g,'').length < 6) return say('Enter all 6 digits.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/user/verifyOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otp.replace(/\s/g,'') }),
      });
      const d = await res.json();
      if (res.ok) { setSignupToken(d.signupToken || ''); setOtpVerified(true); say('Email verified! Set your password.', 'success'); }
      else say(d.msg || 'Invalid OTP. Please try again.', 'error');
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Create account ── */
  const createAccount = async () => {
    if (password !== cnfpassword) return say('Passwords do not match.', 'error');
    if (getStrength(password) < 2) return say('Password is too weak.', 'error');
    if (!agreedToTerms) return say('Please review and accept the Terms of Service and Privacy Policy to continue.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/user/registerpartner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signupToken, password, acceptedTermsVersion: LEGAL_VERSION }),
      });
      if (res.ok) {
        say('Account created! You can now log in.', 'success');
        setTimeout(() => switchTab(true), 1500);
      } else {
        const d = await res.json().catch(() => ({}));
        if (res.status === 401) { setOtpSent(false); setOtpVerified(false); setSignupToken(''); setOtp(''); }
        say(d.msg || 'Registration failed. Try again.', 'error');
      }
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ══════════════ FORGOT PASSWORD ══════════════ */

  /* Step 0 → 1. Also the handler behind <ResendOtp> in step 1.
     The backend answers identically for known and unknown emails, so a
     success here does not confirm the account exists. */
  const sendResetOtp = async () => {
    if (!email) return say('Please enter your email.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/resetpassword/sendResetOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setResetOtpSent(true);
        setOtp('');
        say(d.msg || 'If an account exists for that email, a reset code has been sent.', 'success');
      } else {
        say(d.msg || 'Could not send the reset code. Please try again.', 'error');
      }
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* Step 1 → 2. Trades the OTP for a short-lived, single-use reset token. */
  const verifyResetOtp = async () => {
    if (otp.replace(/\D/g, '').length < 6) return say('Enter all 6 digits.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/resetpassword/verifyResetOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otp.replace(/\D/g, '') }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.resetToken) {
        setResetToken(d.resetToken);
        setResetVerified(true);
        setPassword(''); setCnfPassword('');
        say('Code verified. Set your new password.', 'success');
      } else {
        say(d.msg || 'Invalid code. Please try again.', 'error');
      }
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* Step 2. On success the old session token is dead server-side, so we
     clear it locally too and drop the partner back on the sign-in tab. */
  const submitNewPassword = async () => {
    if (password !== cnfpassword) return say('Passwords do not match.', 'error');
    if (getStrength(password) < 2) return say('Password is too weak.', 'error');
    setLoading(true); say('');
    try {
      const res = await fetch(`${host}/resetpassword/resetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken, password }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        localStorage.removeItem('partnertoken');
        say(d.msg || 'Password updated. You can now sign in.', 'success');
        setTimeout(() => goTo('login'), 1500);
      } else {
        const text = d.msg || 'Could not reset password. Please try again.';
        say(text, 'error');
        // Token is spent or expired — send them back to step 0 rather than
        // letting them retype a password against a token that can't work.
        if (res.status === 400 && /expired|already been used|start again/i.test(text)) {
          setResetVerified(false);
          setResetOtpSent(false);
          setResetToken('');
          setOtp('');
        }
      }
    } catch {
      say('Connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Enter key ── */
  const onKey = (fn) => (e) => { if (e.key === 'Enter') fn(); };

  /* ════════════════════ RENDER ════════════════════ */
  if (isLoggedin) {
    return (
      <div style={{ height: '100vh', width: '100%', overflow: 'hidden', background: 'var(--console-bg, #0F2534)' }}>
        <PartnerHome isLoggedin={isLoggedin} setIsLoggedin={setIsLoggedin} lastLoginTime={lastLoginTime} />
      </div>
    );
  }

  return (
    <>
      
      <div className="p-page">
        <div className="p-mesh" />
        <div className="p-split">

          {/* ── Left panel ── */}
          <div className="p-left">
            <div className="p-brand">
              <img
                src="/production_logo/RestroPilots_Primary_Logo.svg"
                alt="RestroPilots"
                className="p-brand-logo"
              />
            </div>

            <div className="p-hero">
              <div className="p-hero-tag">
                <span>◆</span> Partner Portal
              </div>
              <h1 className="p-hero-title">
                Your restaurant.<br />
                <em>Run exactly the way you built it.</em>
              </h1>
              <p className="p-hero-sub">
                The tools stay out of your way. The menu, the service, the
                relationships with the people you feed — all yours, every
                single day you sign in.
              </p>
            </div>

            <div className="p-features">
              {['Live order management', 'AI enabled Real-time inventory tracking', 'Customer reviews & analytics', 'Multi chain support'].map((f, i) => (
                <div className="p-feature" key={i}>
                  <div className="p-feature-dot" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="p-divider" />

          {/* ── Right panel ── */}
          <div className="p-right">
            <div className="p-form-card">

              <div className="p-mobile-brand">
                <img
                  src="/production_logo/RestroPilots_Primary_Logo.svg"
                  alt="RestroPilots"
                  className="p-brand-logo"
                />
              </div>

              {/* Neither tab reads as active during a reset — the partner is
                  off the sign-in/register axis entirely at that point. */}
              <div className="p-tabs">
                <button className={`p-tab${mode === 'login' ? ' active' : ''}`} onClick={() => switchTab(true)}>Sign In</button>
                <button className={`p-tab${mode === 'register' ? ' active' : ''}`} onClick={() => switchTab(false)}>Register</button>
              </div>

              <div className="p-form-enter" key={mode}>

                {/* ── LOGIN ── */}
                {mode === 'login' && (
                  <>
                    <h2 className="p-form-title">Welcome back</h2>
                    <p className="p-form-subtitle">Sign in to your partner account</p>

                    <Msg {...msg} />

                    <div className="p-field">
                      <label className="p-label">Email</label>
                      <div className="p-input-wrap">
                        <span className="p-input-icon">✉️</span>
                        <input
                          type="email"
                          className="p-input"
                          placeholder="you@restaurant.com"
                          autoComplete="username"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          onKeyDown={onKey(login)}
                        />
                      </div>
                    </div>

                    <PwField
                      label="Password"
                      value={password}
                      onChange={setPassword}
                      autoComplete="current-password"
                      onKeyDown={onKey(login)}
                    />

                    <div className="p-forgot-row">
                      <button type="button" className="p-forgot-btn" onClick={goForgot}>
                        Forgot password?
                      </button>
                    </div>

                    <button
                      className="p-btn p-btn-primary"
                      onClick={login}
                      disabled={loading}
                      style={{ marginTop: 18 }}
                    >
                      {loading ? <><div className="p-spinner" /> Signing in…</> : 'Sign In →'}
                    </button>
                  </>
                )}

                {/* ── REGISTER ── */}
                {mode === 'register' && (
                  <>
                    <h2 className="p-form-title">Create account</h2>
                    <p className="p-form-subtitle">Get your restaurant online in minutes</p>

                    <Steps current={step} />
                    <Msg {...msg} />

                    {/* Step 0: Email */}
                    {step === 0 && (
                      <>
                        <div className="p-field">
                          <label className="p-label">Business Email</label>
                          <div className="p-input-wrap">
                            <span className="p-input-icon">✉️</span>
                            <input
                              type="email"
                              className="p-input"
                              placeholder="owner@restaurant.com"
                              autoComplete="username"
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              onKeyDown={onKey(sendOtp)}
                            />
                          </div>
                        </div>

                        {/* ── Review full document pack ── */}
                        <button
                          type="button"
                          className="p-terms-review-btn"
                          onClick={() => openLegal('terms')}
                        >
                          📄 Review Terms, Privacy Policy & Security Overview
                        </button>

                        {/* ── Acceptance checkbox ── */}
                        <label className={`p-terms-row${agreedToTerms ? ' checked' : ''}`}>
                          <input
                            type="checkbox"
                            className="p-checkbox"
                            checked={agreedToTerms}
                            onChange={e => setAgreedToTerms(e.target.checked)}
                          />
                          <span className="p-terms-text">
                            I agree to Restropilots'{' '}
                            <span className="p-terms-link" onClick={(e) => { e.preventDefault(); openLegal('terms'); }}>
                              Terms of Service
                            </span>{' '}and{' '}
                            <span className="p-terms-link" onClick={(e) => { e.preventDefault(); openLegal('dpa'); }}>
                              Data Processing Addendum
                            </span>, and I have read the{' '}
                            <span className="p-terms-link" onClick={(e) => { e.preventDefault(); openLegal('privacy'); }}>
                              Privacy Policy
                            </span>.
                          </span>
                        </label>

                        <button className="p-btn p-btn-primary" onClick={sendOtp} disabled={loading || !agreedToTerms}>
                          {loading ? <><div className="p-spinner" /> Sending…</> : 'Send OTP →'}
                        </button>
                      </>
                    )}

                    {/* Step 1: OTP */}
                    {step === 1 && (
                      <>
                        <p className="p-sent-to">
                          Enter the 6-digit code sent to <strong>{email}</strong>
                        </p>
                        <OtpInput value={otp} onChange={setOtp} />

                        {/* ── Resend OTP ── */}
                        <ResendOtp onResend={sendOtp} loading={loading} />

                        <button className="p-btn p-btn-primary" onClick={verifyOTP} disabled={loading} style={{ marginTop: 16 }}>
                          {loading ? <><div className="p-spinner" /> Verifying…</> : 'Verify OTP →'}
                        </button>
                        <button className="p-btn p-btn-ghost" onClick={() => { setOtpSent(false); setOtp(''); say(''); }} style={{ marginTop: 8 }}>
                          ← Change Email
                        </button>
                      </>
                    )}

                    {/* Step 2: Password */}
                    {step === 2 && (
                      <>
                        <PwField
                          label="Password"
                          value={password}
                          onChange={setPassword}
                          showStrength
                          autoComplete="new-password"
                        />
                        <PwField
                          label="Confirm Password"
                          value={cnfpassword}
                          onChange={setCnfPassword}
                          autoComplete="new-password"
                          onKeyDown={onKey(createAccount)}
                        />
                        <button className="p-btn p-btn-primary" onClick={createAccount} disabled={loading} style={{ marginTop: 16 }}>
                          {loading ? <><div className="p-spinner" /> Creating…</> : 'Create Account ✓'}
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* ── FORGOT PASSWORD ── */}
                {mode === 'forgot' && (
                  <>
                    <h2 className="p-form-title">Reset password</h2>
                    <p className="p-form-subtitle">
                      We'll email you a 6-digit code to confirm it's you
                    </p>

                    <Steps current={forgotStep} labels={['Email', 'Verify', 'New Password']} />
                    <Msg {...msg} />

                    {/* Step 0: Email */}
                    {forgotStep === 0 && (
                      <>
                        <div className="p-field">
                          <label className="p-label">Account Email</label>
                          <div className="p-input-wrap">
                            <span className="p-input-icon">✉️</span>
                            <input
                              type="email"
                              className="p-input"
                              placeholder="owner@restaurant.com"
                              autoComplete="username"
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              onKeyDown={onKey(sendResetOtp)}
                            />
                          </div>
                        </div>

                        <button className="p-btn p-btn-primary" onClick={sendResetOtp} disabled={loading}>
                          {loading ? <><div className="p-spinner" /> Sending…</> : 'Send reset code →'}
                        </button>
                      </>
                    )}

                    {/* Step 1: OTP */}
                    {forgotStep === 1 && (
                      <>
                        <p className="p-sent-to">
                          Enter the 6-digit code sent to <strong>{email}</strong>. It
                          expires in 10 minutes.
                        </p>
                        <OtpInput value={otp} onChange={setOtp} />

                        <ResendOtp onResend={sendResetOtp} loading={loading} />

                        <button className="p-btn p-btn-primary" onClick={verifyResetOtp} disabled={loading} style={{ marginTop: 16 }}>
                          {loading ? <><div className="p-spinner" /> Verifying…</> : 'Verify code →'}
                        </button>
                        <button
                          className="p-btn p-btn-ghost"
                          onClick={() => { setResetOtpSent(false); setOtp(''); say(''); }}
                          style={{ marginTop: 8 }}
                        >
                          ← Change Email
                        </button>
                      </>
                    )}

                    {/* Step 2: New password */}
                    {forgotStep === 2 && (
                      <>
                        <PwField
                          label="New Password"
                          value={password}
                          onChange={setPassword}
                          showStrength
                          autoComplete="new-password"
                        />
                        <PwField
                          label="Confirm New Password"
                          value={cnfpassword}
                          onChange={setCnfPassword}
                          autoComplete="new-password"
                          onKeyDown={onKey(submitNewPassword)}
                        />
                        <button className="p-btn p-btn-primary" onClick={submitNewPassword} disabled={loading} style={{ marginTop: 16 }}>
                          {loading ? <><div className="p-spinner" /> Updating…</> : 'Update password ✓'}
                        </button>
                      </>
                    )}

                    <p className="p-back-row">
                      Remembered it?{' '}
                      <button type="button" onClick={() => goTo('login')}>Back to sign in</button>
                    </p>
                  </>
                )}

                {mode !== 'forgot' && (
                  <p className="p-toggle">
                    {mode === 'login'
                      ? <>New partner? <span onClick={() => switchTab(false)}>Create account</span></>
                      : <>Have an account? <span onClick={() => switchTab(true)}>Sign in</span></>}
                  </p>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Legal document reader modal ── */}
      <LegalDocuments
        open={showLegal}
        onClose={() => setShowLegal(false)}
        initialTab={legalTab}
        onAccept={() => setAgreedToTerms(true)}
      />
    </>
  );
};

export default Partner;